import * as React from 'react';

/**
 * A single, app-level "page dots" indicator (rendered by App near the bottom,
 * above the mobile bottom-nav) that reflects the *currently visible* swipeable
 * screen's sub-tabs — a hint that you can swipe here, plus your position. Only
 * one swipeable screen is on-screen at a time, so it publishes its state here via
 * useSwipeDots(); App reads it with useSwipeDotsValue(). Tapping a dot switches tab.
 */

export type SwipeDots = {
  count: number;
  index: number;
  onSelect: (i: number) => void;
} | null;

type Ctx = { dots: SwipeDots; setDots: React.Dispatch<React.SetStateAction<SwipeDots>> };

const SwipeDotsCtx = React.createContext<Ctx>({ dots: null, setDots: () => {} });

export function SwipeDotsProvider({ children }: { children: React.ReactNode }) {
  const [dots, setDots] = React.useState<SwipeDots>(null);
  return <SwipeDotsCtx.Provider value={{ dots, setDots }}>{children}</SwipeDotsCtx.Provider>;
}

/** App-side: read the active screen's dots (or null when nothing swipeable is shown). */
export function useSwipeDotsValue(): SwipeDots {
  return React.useContext(SwipeDotsCtx).dots;
}

/**
 * Screen-side: publish this screen's swipeable sub-tabs to the shared indicator.
 * Pass the same ordered tab list / current tab / setter you gave useSwipeTabs.
 * `enabled` lets a screen publish only while its swipe area is actually visible
 * (e.g. the planner is open, or the Stats 1RM view is active); it also keeps a
 * hidden/duplicate mount from hijacking the indicator.
 */
export function useSwipeDots<T extends string>(
  order: readonly T[],
  current: T,
  onChange: (next: T) => void,
  enabled = true,
) {
  const { setDots } = React.useContext(SwipeDotsCtx);
  const orderRef = React.useRef(order);
  const onChangeRef = React.useRef(onChange);
  orderRef.current = order;
  onChangeRef.current = onChange;

  const count = order.length;
  const index = order.indexOf(current);

  React.useEffect(() => {
    if (!enabled || count <= 1 || index < 0) {
      setDots(null);
      return;
    }
    setDots({ count, index, onSelect: (i) => onChangeRef.current(orderRef.current[i]!) });
    return () => setDots(null);
  }, [enabled, count, index, setDots]);
}
